import {useEffect, useState, useRef} from "@wordpress/element";
import {Grid, Container, Stack} from "@mui/material";
import {MapContainer, Marker, TileLayer, Popup, useMap, LayersControl, AttributionControl} from "react-leaflet";
import Control from "react-leaflet-custom-control";
import L from 'leaflet';
import SpacesList from "../../spaces-list/components/SpacesList";
import {SpaceCard} from "../../spaces-grid/components/SpacesGrid";

import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import googleIcon from '../../assets/google_light.png';
import {SpacesControls} from "../../common/SpacesControls";
import {applyState, toParam} from "../../common/util";

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
});
L.Marker.prototype.options.icon = DefaultIcon;

function MapResizer({trigger}) {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
        }, 3000);
    }, [trigger]);
    return <></>;
}

function MapZoomer({center, spaces}) {
    const map = useMap();
    useEffect(() => {
        if (spaces.length === 0) {
            map.setView(center, 10);
            setTimeout(() => {
                map.invalidateSize();
            }, 3000);
            return;
        }
        let points = spaces.map((s) => [s['Latitude'], s['Longitude']]);
        let bounds = new L.LatLngBounds(points);
        if (spaces.length > 3) {
            map.fitBounds(bounds);
        } else {
            map.panTo(bounds.getCenter());
        }
    }, [spaces]);
    return <></>;
}

function SpaceMarker({space, selected, onClick, disablePopup = false}) {
    const spaceID = space['Space_ID'];
    const map = useMap();
    const markerRef = useRef(null);
    const position = [space['Latitude'], space['Longitude']];

    useEffect(() => {
        if (selected?.['Space_ID'] === space['Space_ID']) {
            map.setView(position);
            if (!disablePopup && markerRef.current) {
                markerRef.current.openPopup();
            }
        } else {
            if (markerRef.current && markerRef.current.isPopupOpen()) {
                markerRef.current.closePopup();
            }
        }

    }, [selected, markerRef]);

    return (
        <Marker key={`space-marker-${spaceID}`} position={position}
                ref={markerRef} eventHandlers={{click: () => onClick?.(space)}}>
            <Popup key={`space-marker-tooltip-${spaceID}`}>
                <Container disableGutters sx={{
                    width: 'clamp(234px, 3vw, 313px)',
                    height: 'clamp(291px, 3.5vw, 389px)',
                }}>
                    <SpaceCard space={space} dense mini/>
                </Container>
            </Popup>
        </Marker>
    );
}

const {BaseLayer} = LayersControl;

function LayerControl({}) {
    return (
        <LayersControl position='topright'>
            <BaseLayer name='Street' checked>
                <TileLayer key='spaces-map-street-tile-layer'
                           url='https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
                           maxZoom={20}
                           subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                           attribution='Map Data &copy; 2024 Google'
                />
            </BaseLayer>
            <BaseLayer name='Satellite'>
                <TileLayer key='spaces-map-satellite-tile-layer'
                           url='https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}'
                           maxZoom={20}
                           subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                           attribution='Map Data &copy; 2024 Google'
                />
            </BaseLayer>
            <BaseLayer name='Terrain'>
                <TileLayer key='spaces-map-hybrid-tile-layer'
                           url='https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'
                           maxZoom={20}
                           subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                           attribution='Map Data &copy; 2024 Google'
                />
            </BaseLayer>
            <AttributionControl position='bottomright' prefix=''/>
        </LayersControl>
    );
}

function SpacesMap({attributes}) {
    const [city, setCity] = useState(toParam(attributes.city));
    const [propertyType, setPropertyType] = useState(toParam(attributes.propertyType));
    const [subType, setSubType] = useState(toParam(attributes.subType));
    const [availType, setAvailType] = useState(toParam(attributes.availType));
    const [minSqft, setMinSqft] = useState(toParam(attributes.minSqft));
    const [maxSqft, setMaxSqft] = useState(toParam(attributes.maxSqft));
    const [minRent, setMinRent] = useState(toParam(attributes.minRent));
    const [maxRent, setMaxRent] = useState(toParam(attributes.maxRent));
    const [spaces, setSpaces] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [selected, setSelected] = useState(null);
    const [mapWidth, setMapWidth] = useState(null);
    const [error, setError] = useState(null);
    const centerPoint = [37.8974, -122.0820]; //just west of I-680/CA-24 intersection

    useEffect(() => {
        const resizeObserver = new ResizeObserver((event) => {
            setMapWidth(event[0].contentBoxSize[0].inlineSize);
        });
        resizeObserver.observe(document.getElementById("spaces-map-container"));
    });

    if (error) {
        return <div>Error: {error.message}</div>
    } else {
        return <>
            <Stack key='spaces-map-stack' spacing={2}
                   sx={{width: '100%', minWidth: '320px', alignContent: 'center', justifyContent: 'center'}}>
                <SpacesControls key='spaces-map-controls' city={city}
                                onCityChange={setCity}
                                propertyType={propertyType}
                                onTypeChange={setPropertyType}
                                subType={subType}
                                onSubTypeChange={setSubType}
                                availType={availType}
                                onAvailTypeChange={setAvailType}
                                minSqft={minSqft}
                                maxSqft={maxSqft}
                                onSqftChange={(range) => {
                                    setMinSqft(range[0]);
                                    setMaxSqft(range[1]);
                                }}
                                minRent={minRent}
                                maxRent={maxRent}
                                onRentChange={(range) => {
                                    setMinRent(range[0]);
                                    setMaxRent(range[1]);
                                }}
                                onError={setError}
                                fullWidth/>
                <Grid container spacing={1} sx={{width: '100%', minWidth: '320px'}}>
                    <Grid item key='spaces-map-grid' sm={12} lg={7} xl={7}>
                        <MapContainer key='spaces-map-container' id='spaces-map-container'
                                      center={centerPoint} zoom={10}
                                      style={{width: '100%', height: '100%', minWidth: '320px', minHeight: '320px'}}
                                      whenReady={() => setLoaded(true)}
                                      attributionControl={false}>
                            <MapResizer key='spaces-map-resizer' trigger={loaded}/>
                            <MapZoomer key='spaces-map-zoomer' center={centerPoint} spaces={spaces}/>
                            <LayerControl/>
                            <Control position='bottomleft'>
                                <img alt='Google' src={googleIcon} style={{height: '16px'}}/>
                            </Control>
                            {spaces.map((space) => <SpaceMarker key={`spaces-map-space-marker-${space['Space_ID']}`}
                                                                space={space} disablePopup={mapWidth < 400}
                                                                selected={selected} setSelected={setSelected}/>)}
                        </MapContainer>
                    </Grid>
                    <Grid item key='spaces-map-list' sm={12} lg={5} xl={5}>
                        <Container key='spaces-map-list-container' disableGutters sx={{width: '100%'}}>
                            <SpacesList city={city}
                                        propertyType={propertyType}
                                        subType={subType}
                                        availType={availType}
                                        minSqft={minSqft}
                                        maxSqft={maxSqft}
                                        onSqftChange={(range) => {
                                            setMinSqft(range[0]);
                                            setMaxSqft(range[1]);
                                        }}
                                        minRent={minRent}
                                        maxRent={maxRent}
                                        onRentChange={(range) => {
                                            setMinRent(range[0]);
                                            setMaxRent(range[1]);
                                        }}
                                        scrollable={true} showControls={false}
                                        onSpacesChanged={setSpaces}
                                        onSpaceHover={setSelected}/>
                        </Container>
                    </Grid>
                </Grid>
            </Stack>
        </>;
    }
}

export default SpacesMap;
